import { useEffect, useMemo, useRef } from 'react';
import { debounce } from 'throttle-debounce';

export function useListingScrollEffect({
  action,
  delta,
  enabled,
}: {
  action: () => void;
  delta: number;
  enabled: boolean;
}): void {
  const initialScroll = useRef(0);
  const capture = useRef(false);

  const debouncedUpdateDelta = useMemo(
    () =>
      debounce(50, newDelta => {
        if (Math.abs(newDelta) > delta) {
          action();
        }
      }),
    [action, delta],
  );

  useEffect(() => {
    if (enabled) {
      capture.current = true;
      initialScroll.current = window.scrollY;
    } else {
      capture.current = false;
      initialScroll.current = 0;
    }
  }, [enabled]);

  useEffect(() => {
    const handleScroll = (): void => {
      if (capture.current) {
        debouncedUpdateDelta(window.scrollY - initialScroll.current);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return (): void => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [debouncedUpdateDelta]);
}
